import USStatesWithISOCodes from '@liveauctioneers/caterwaul-components/lib/Intl/USStatesWithISOCodes';
import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { Radio } from '@liveauctioneers/hammer-ui-core/radio';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { colorBackgroundPrimary } from '@liveauctioneers/hammer-ui-theme/vars';
import { ComponentProps, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { useServerSideFacetExpansion } from '@/pages/Search/SearchPage/hooks/useServerSideFacetExpansion';
import { COUNTRY_VIEW_MORE_SIZE, CurrentOpenMenu, VIEW_MORE_CUTOFF_HEIGHT } from '@/pages/Search/SearchPage/types';
import { getRoundedNumberText } from '@/pages/Search/SearchPage/utils/getRoundedNumberText';
import { useAppSelector } from '@/redux/hooks';
import { getDefaultAddressCountryCode, getDefaultAddressPostalCode } from '@/redux/modules/account/user/user.selectors';
import { getMergedOptionFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import { Distance } from '@/types/Distance';
import { FacetOption } from '@/types/FacetOption';
import FacetTypes from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';
import { FilterAction } from '@/types/SearchFacet';
import { CanadianProvincesWithISOCodes, US_POSTAL_CODE_LENGTH, US_ZIP_CODE_REGEX } from '@/utils/address';
import { QuickFilterName, useTrackQuickFilterPillClicked } from '@/utils/amplitude/analytics/searchPage';
import {
    modifyParamsRemoveCountryAndDistance,
    modifyParamsRemovePage,
    modifyParamsReplaceCountry,
    modifyParamsWithDistance,
    parseQueryParams,
} from '@/utils/queryParams';

import { ConditionalMenuContainer } from '../ConditionalMenuContainer/ConditionalMenuContainer';
import { LocationRangeFilter } from './components/LocationRangeFilter/LocationRangeFilter';
import { StateFilter } from './components/StateFilter/StateFilter';
import { UpdateLocationModal } from './components/UpdateLocationModal/UpdateLocationModal';

const PINNED_COUNTRIES = ['US', 'UK', 'CA'];

const sortByPinnedThenCount = (firstOption: FacetOption, secondOption: FacetOption) => {
    const firstPinIndex = PINNED_COUNTRIES.indexOf(firstOption.id);
    const secondPinIndex = PINNED_COUNTRIES.indexOf(secondOption.id);
    if (firstPinIndex !== -1 && secondPinIndex !== -1) {
        return firstPinIndex - secondPinIndex;
    }
    if (firstPinIndex !== -1) {
        return -1;
    }
    if (secondPinIndex !== -1) {
        return 1;
    }
    return (secondOption.count ?? 0) - (firstOption.count ?? 0);
};

type Props = {
    currentOpenMenu?: CurrentOpenMenu;
    distance: Distance;
    isAllFilters?: boolean;
    isArchivedSearch: boolean;
    onMenuClose: () => void;
    onMenuOpen: (currentMenu: CurrentOpenMenu) => void;
};

// Intentionally inlines ViewMore instead of delegating to FacetCard.
// LocationFilter is wrapped in ConditionalMenuContainer (not Accordion) and
// renders a "Within X miles" radio plus a nested StateFilter alongside the
// country list, none of which fit FacetCard's shape. Keeps the
// scroll-reset + loading-spinner pattern in sync with FacetCard manually --
// if either is changed there, mirror it here.
export const LocationFilter = ({
    currentOpenMenu,
    distance,
    isAllFilters,
    isArchivedSearch,
    onMenuClose,
    onMenuOpen,
}: Props) => {
    const { formatMessage } = useIntl();
    const trackQuickFilterPillClicked = useTrackQuickFilterPillClicked();
    const locationFacet = useAppSelector((state) => getMergedOptionFacet(state, FacetTypes.COUNTRY_CODE_ID));
    const { options = [] } = locationFacet;
    const { search } = useLocation();

    const defaultAddressPostalCode = useAppSelector(getDefaultAddressPostalCode);
    const defaultAddressCountryCode = useAppSelector(getDefaultAddressCountryCode);
    const defaultZipCode = useMemo(() => {
        if (
            defaultAddressCountryCode === 'US' &&
            defaultAddressPostalCode &&
            US_ZIP_CODE_REGEX.test(defaultAddressPostalCode)
        ) {
            return defaultAddressPostalCode.slice(0, US_POSTAL_CODE_LENGTH);
        }
        return undefined;
    }, [defaultAddressCountryCode, defaultAddressPostalCode]);

    const queryParams = parseQueryParams(search);
    const countryFacetParam = queryParams.country;
    const stateFacetParam = queryParams[paramTypes.STATE_ID];
    const navigateWithParams = useNavigateSearchPageWithParams();
    const showStateFilter = (countryFacetParam === 'US' || countryFacetParam === 'CA') && !distance.zipCode;

    const [showLocationModal, setShowLocationModal] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const { isLoading: viewMoreIsLoading, onViewMoreClick } = useServerSideFacetExpansion({
        facetId: FacetTypes.COUNTRY_CODE_ID,
        size: COUNTRY_VIEW_MORE_SIZE,
    });

    // Reset the local expand mirror on URL change so the inner overflow class
    // re-applies cleanly after a filter is applied.
    useEffect(() => {
        setIsExpanded(false);
    }, [search]);

    const handleViewMoreChange = useCallback((expanded: boolean) => {
        setIsExpanded(expanded);
        if (!expanded && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, []);

    const handleOpenLocationModal = useCallback(() => {
        setShowLocationModal(true);
    }, []);

    const handleWithinRadioSelect = useCallback(() => {
        if (distance?.zipCode) {
            return;
        }
        setShowLocationModal(true);
    }, [distance?.zipCode]);

    const handleLocationModalClose = useCallback(() => {
        setShowLocationModal(false);
    }, []);

    const handleLocationModalApply = useCallback(
        (zipCode: string, distanceValue: number) => {
            const keepMenuOpenValue = isAllFilters ? CurrentOpenMenu.AllFilters : CurrentOpenMenu.Location;
            const country = 'US';
            const measurement = 'mile';
            let newParams = modifyParamsWithDistance(queryParams, country, zipCode, String(distanceValue), measurement);
            newParams = modifyParamsRemovePage(newParams);

            navigateWithParams({
                checkForPageRemoval: true,
                currentOpenMenu: keepMenuOpenValue,
                filterAction: FilterAction.LocationChanged,
                params: newParams,
                preventScrollReset: true,
            });
            setShowLocationModal(false);
        },
        [isAllFilters, navigateWithParams, queryParams]
    );

    const handleMenuOpen: ComponentProps<typeof ConditionalMenuContainer>['onMenuOpen'] = useCallback(
        (menu) => {
            if (!isAllFilters) {
                trackQuickFilterPillClicked(QuickFilterName.Location);
            }
            onMenuOpen(menu);
        },
        [isAllFilters, onMenuOpen, trackQuickFilterPillClicked]
    );

    const selectedDistanceFacetName = useMemo(() => {
        const selectedCountryFacet = options.find((option) => option.id === countryFacetParam);

        if (distance.zipCode) {
            return formatMessage(
                { id: 'searchPage.facets.withinDistanceOf' },
                { distance: `${distance.space} miles`, location: distance.zipCode }
            );
        }

        let stateName: string | undefined;
        if (stateFacetParam) {
            const regions = countryFacetParam === 'CA' ? CanadianProvincesWithISOCodes : USStatesWithISOCodes;
            stateName = regions.find((r) => r.code === stateFacetParam)?.name;
        }

        return stateName ? `${stateName}, ${selectedCountryFacet?.name}` : selectedCountryFacet?.name;
    }, [countryFacetParam, distance.space, distance.zipCode, formatMessage, options, stateFacetParam]);

    const sortedOptions = useMemo(
        () =>
            options
                .filter((option: FacetOption) => option.count > 0 || option.id === countryFacetParam)
                .sort(sortByPinnedThenCount),
        [options, countryFacetParam]
    );

    const onCountryOptionClick = useCallback(
        (option?: FacetOption) => {
            const keepMenuOpenValue = isAllFilters ? CurrentOpenMenu.AllFilters : CurrentOpenMenu.Location;
            const isDeselection = !option || (option.id === countryFacetParam && !distance.zipCode);

            if (isDeselection) {
                let newParams = modifyParamsRemoveCountryAndDistance(queryParams);
                newParams = modifyParamsRemovePage(newParams);
                navigateWithParams({
                    checkForPageRemoval: true,
                    currentOpenMenu: keepMenuOpenValue,
                    filterAction: FilterAction.LocationRemoved,
                    params: newParams,
                    preventScrollReset: true,
                });
            } else {
                let newParams = modifyParamsRemoveCountryAndDistance(queryParams);
                newParams = modifyParamsReplaceCountry(newParams, option.id);
                newParams = modifyParamsRemovePage(newParams);
                navigateWithParams({
                    checkForPageRemoval: true,
                    currentOpenMenu: keepMenuOpenValue,
                    filterAction: FilterAction.LocationChanged,
                    params: newParams,
                    preventScrollReset: true,
                });
            }
        },
        [countryFacetParam, distance.zipCode, isAllFilters, navigateWithParams, queryParams]
    );

    const onRemoveChipCountryAndDistanceValue = useCallback(
        (event: MouseEvent<HTMLInputElement>) => {
            event.preventDefault();
            let newParams = modifyParamsRemoveCountryAndDistance(queryParams);
            newParams = modifyParamsRemovePage(newParams);
            navigateWithParams({
                checkForPageRemoval: true,
                filterAction: FilterAction.LocationRemoved,
                params: newParams,
                preventScrollReset: true,
            });
        },
        [navigateWithParams, queryParams]
    );

    return (
        <>
            <ConditionalMenuContainer
                accordionDefaultOpen
                currentOpenMenu={currentOpenMenu}
                currentOpenMenuId={CurrentOpenMenu.Location}
                isAllFilters={isAllFilters}
                onMenuClose={onMenuClose}
                onMenuOpen={handleMenuOpen}
                title={formatMessage({ id: 'searchPage.facets.itemLocation' })}
                trigger={
                    <Chip
                        onClose={onRemoveChipCountryAndDistanceValue}
                        selected={Boolean(selectedDistanceFacetName)}
                        style="singleFilter"
                    >
                        {selectedDistanceFacetName || formatMessage({ id: 'searchPage.facets.itemLocation' })}
                    </Chip>
                }
            >
                <Radio.Option
                    checked={!countryFacetParam && !distance.zipCode}
                    onChange={() => onCountryOptionClick()}
                >
                    {formatMessage({ id: 'searchPage.facets.anywhere' })}
                </Radio.Option>

                {!isArchivedSearch && (
                    <LocationRangeFilter
                        distance={distance}
                        onOpenModal={handleOpenLocationModal}
                        onRadioSelect={handleWithinRadioSelect}
                    />
                )}

                <ViewMore
                    collapseText={formatMessage({ id: 'view_less' })}
                    expandText={formatMessage({ id: 'view_more' })}
                    fadeBackgroundColor={`rgb(var(${colorBackgroundPrimary}))`}
                    key={search}
                    onChange={handleViewMoreChange}
                    onClick={onViewMoreClick}
                    textCutoffHeight={VIEW_MORE_CUTOFF_HEIGHT}
                >
                    <div
                        className={cn('flex max-h-[400px] flex-col gap-xs overflow-y-hidden', {
                            'overflow-y-auto': isExpanded,
                        })}
                        ref={scrollContainerRef}
                    >
                        {sortedOptions.map((option: FacetOption) => (
                            <div key={option.id}>
                                <Radio.Option
                                    checked={option.id === countryFacetParam && !distance.zipCode}
                                    onChange={() => {
                                        onCountryOptionClick(option);
                                    }}
                                    onClick={() => {
                                        // Clicking the already-selected radio does not fire `onChange`; we still need
                                        // to run deselection (navigate back to “Anywhere”).
                                        if (option.id === countryFacetParam && !distance.zipCode) {
                                            onCountryOptionClick(option);
                                        }
                                    }}
                                >
                                    {option.name}
                                    {!distance.zipCode && (
                                        <>
                                            &nbsp;
                                            <span className="text-text-secondary">
                                                ({getRoundedNumberText({ totalFound: option.count })})
                                            </span>
                                        </>
                                    )}
                                </Radio.Option>
                                {showStateFilter && option.id === countryFacetParam && (
                                    <StateFilter
                                        countryCode={countryFacetParam}
                                        isAllFilters={isAllFilters}
                                    />
                                )}
                            </div>
                        ))}
                        {isExpanded && viewMoreIsLoading && (
                            <div className="flex justify-center py-xs">
                                <Loading />
                            </div>
                        )}
                    </div>
                </ViewMore>
            </ConditionalMenuContainer>
            {!isArchivedSearch && (
                <UpdateLocationModal
                    defaultZipCode={defaultZipCode}
                    distance={distance}
                    onApply={handleLocationModalApply}
                    onClose={handleLocationModalClose}
                    show={showLocationModal}
                />
            )}
        </>
    );
};
